import React, { useEffect, useState } from 'react';
import { reservationEnums } from '../../data/reservationStatusEnums';
import FeatureFlag from '../../components/FeatureFlag';
import dayjs from 'dayjs';
import { ReservationModel } from '@common/modelDefinition';
import translate from '@data/translations';
import { ReactComponent as Tick } from '../../assets/custom/icons/tick.svg';
import { ReactComponent as Close } from '../../assets/custom/icons/ex.svg';
import useStore from '@data/state/zustand';
import { unstable_batchedUpdates } from 'react-dom';
import { CustomDayPicker } from '@components/DayPicker';

interface FromToFilterProps {
    filterOutRoomsById: any[];
    setHideRoomByIdFilter: Function;
    reservations: ReservationModel[];
    setAvailableFromAndToTimestamps: Function;
    toggleGroupReservations: Function;
    toggleSelectRoomForReservation: Function;
    setNumberOfBeds: Function;
    setNumberOfSpareBeds: Function;
    groupReservations: boolean;
    numberOfBeds: number | string;
    numberOfSpareBeds: number | string;
    showReservationsList: boolean;
    setShowReservationsList: Function;
    toogleOccupiedRooms: Function;
    showOccupiedRooms: boolean;
    availableFromTimestamp: number;
    availableToTimestamp: number | null;
    clearToolbar: Function;
}

function FromToFilter(props: FromToFilterProps) {
    const [stayInDays, setStayInDays] = useState(1 as number);
    const [availableFrom2 /* setAvailableFrom2 */] = useState('' as string);
    const [availabilityFiltersShown, setAvailabilityFiltersShown] = React.useState(false as boolean);
    const availableFrom = props.availableFromTimestamp;
    const availableTo = props.availableToTimestamp;

    const otherSettings = useStore((appState: any) => appState.otherSettings);

    useEffect(() => {
        const loadData = async () => {
            if (availableTo !== null) {
                calculateAvailableRoomsInRange();
            } else {
                if (props.filterOutRoomsById.length > 0) {
                    props.setHideRoomByIdFilter([]);
                }
                //
            }
        };

        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [availableFrom, availableTo]);

    const calculateAvailableRoomsInRange = () => {
        if (props.reservations) {
            let roomIdWithReservationOverlapping: number[] = [];
            props.reservations?.forEach((r) => {
                let b = dayjs(availableFrom).toDate();
                b.setHours(22, 59, 59);
                const availableFrom_adjusted = b.getTime();

                let a = dayjs(availableTo).add(-1, 'd').toDate();
                a.setHours(22, 59, 59);
                const availableTo_adjusted = a.getTime();

                if (r.checkInTimestamp > availableFrom_adjusted && r.checkInTimestamp < availableTo_adjusted) {
                    //test checkin overlap
                    if (r.statusEnum !== reservationEnums.closed && r.statusEnum !== reservationEnums.canceled) {
                        roomIdWithReservationOverlapping.push(r.roomInfoId);
                    }
                }
                if (r.checkOutTimestamp > availableFrom_adjusted && r.checkOutTimestamp < availableTo_adjusted) {
                    //test checkout overlap
                    if (r.statusEnum !== reservationEnums.closed && r.statusEnum !== reservationEnums.canceled) {
                        roomIdWithReservationOverlapping.push(r.roomInfoId);
                    }
                }
                if (r.checkInTimestamp === availableFrom_adjusted && r.checkOutTimestamp === availableTo_adjusted) {
                    if (r.statusEnum !== reservationEnums.closed && r.statusEnum !== reservationEnums.canceled) {
                        roomIdWithReservationOverlapping.push(r.roomInfoId);
                    }
                }

                if (r.checkInTimestamp < availableFrom_adjusted && r.checkOutTimestamp > availableFrom_adjusted) {
                    if (r.statusEnum !== reservationEnums.closed && r.statusEnum !== reservationEnums.canceled) {
                        roomIdWithReservationOverlapping.push(r.roomInfoId);
                    }
                }
            });
            roomIdWithReservationOverlapping = [...new Set(roomIdWithReservationOverlapping)];
            let hasChangedFromLastRender =
                props.filterOutRoomsById.join(',') !== roomIdWithReservationOverlapping.join(',');
            if (hasChangedFromLastRender) {
                if (props.setHideRoomByIdFilter) {
                    props.setHideRoomByIdFilter(roomIdWithReservationOverlapping);
                }
            }
        }
    };

    const getHourAndMinuteObj = (time: any) => {
        let newTime = time ? time.split(':') : [12, 0]; //add default time if setting does not exist
        let hour = Number(newTime[0]);
        let minute = Number(newTime[1]);
        return { hour, minute };
    };

    const setInputs = (availableFrom: any, availableTo: any, stayInDays: number) => {
        const { checkInTime = '14:00', checkOutTime = '10:00' } = otherSettings;
        let checkInHourAndMinute = getHourAndMinuteObj(checkInTime);
        let checkOutHourAndMinute = getHourAndMinuteObj(checkOutTime);
        const availableFromAdjusted = new Date(
            new Date(availableFrom).setHours(checkInHourAndMinute.hour, checkInHourAndMinute.minute, 0, 0)
        ).getTime();

        if (stayInDays === 0) {
            setStayInDays(0);
            // setAvailableTo(null);
            props.setAvailableFromAndToTimestamps(availableFromAdjusted, null, null);
        } else {
            const availableToAdjusted = new Date(
                new Date(availableTo).setHours(checkOutHourAndMinute.hour, checkOutHourAndMinute.minute, 0, 0)
            ).getTime();

            // setAvailableFrom(availableFromAdjusted);
            setStayInDays(stayInDays);
            // setAvailableTo(availableToAdjusted);
            props.setAvailableFromAndToTimestamps(availableFromAdjusted, availableToAdjusted, stayInDays);
        }
    };

    const handleAvailableFromDatePick = (pick: any) => {
        try {
            if (stayInDays === 0) {
                let availableTo = pick.getTime() + 24 * 60 * 60 * 1000;
                setInputs(pick.getTime(), availableTo, 1);
            } else {
                let availableTo = pick.getTime() + 24 * 60 * 60 * 1000 * Number(stayInDays);
                setInputs(pick.getTime(), availableTo, stayInDays);
            }
        } catch (err) {
            console.warn(err);
        }
    };

    const handleAvailableToDatePick = (pick: any) => {
        try {
            let stayInDays = Math.ceil((pick.getTime() - availableFrom) / (24 * 60 * 60 * 1000));
            setInputs(availableFrom, pick.getTime(), stayInDays);
            props.toggleGroupReservations(true);
        } catch (err) {
            console.warn(err);
        }
    };

    const changeStayInDays = (e: any) => {
        let stayInDays = e.target.value;
        let availableTo = null;
        if (e.target.value !== '') {
            stayInDays = Number(e.target.value);
            availableTo = availableFrom + stayInDays * 24 * 60 * 60 * 1000;
        }
        setInputs(availableFrom, availableTo, stayInDays);
    };

    const clearHandler = () => {
        setInputs(Date.now(), null, 0);

        unstable_batchedUpdates(() => {
            props.clearToolbar();
        });
    };

    if (window.innerWidth < 768) {
        return (
            <div>
                <h1>{translate('Reservation table')}</h1>

                <div className="reservation-filters-wrapper-mobile">
                    <div className="display-flex">
                        <span
                            className="reservation-filters-wrapper-mobile-button button-white-w-icon"
                            onClick={() => {
                                setAvailabilityFiltersShown(!availabilityFiltersShown);
                            }}
                        >
                            <Tick /> {translate('Availability filters')}:&ensp;
                        </span>
                    </div>
                    {availabilityFiltersShown ? (
                        <div className="availabilty-filters-mobile">
                            <div className="availabilty-filters-mobile-header">
                                <span>{translate('Availability')}</span>
                                <span
                                    onClick={() => {
                                        setAvailabilityFiltersShown(false);
                                    }}
                                >
                                    <Close />
                                </span>
                            </div>
                            <div className="DayPickerInput-wrapper">
                                <CustomDayPicker
                                    value={availableFrom ? new Date(availableFrom)?.getTime() : null}
                                    onSelect={async (d: any) => {
                                        handleAvailableFromDatePick(d);
                                    }}
                                    disabledBefore={new Date()}
                                />
                                <CustomDayPicker
                                    value={
                                        availableTo
                                            ? new Date(availableTo)?.getTime()
                                            : availableFrom2 === ''
                                            ? new Date().getTime()
                                            : new Date(availableFrom2)?.getTime()
                                    }
                                    onSelect={async (e: any) => {
                                        handleAvailableFromDatePick(e);
                                    }}
                                    disabledBefore={availableFrom ? new Date(availableFrom) : new Date()}
                                    disabledAfter={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
                                />
                            </div>
                            {/* <small>&ensp;{window.translate('')}&ensp;</small> */}
                            <input
                                style={{ width: '100%' }}
                                min={0}
                                value={stayInDays === null ? '' : stayInDays}
                                onChange={(e: any) => changeStayInDays(e)}
                                className="input"
                                type="number"
                                placeholder={translate('Days')}
                                title={translate('Days')}
                            />
                            <input
                                onChange={(e: any) => props.setNumberOfBeds(e)}
                                style={{ width: '100%' }}
                                className="input"
                                type="number"
                                title={translate('Beds')}
                                placeholder={translate('Beds')}
                                value={props.numberOfBeds}
                                min={0}
                            />
                            <input
                                onChange={(e: any) => props.setNumberOfSpareBeds(e)}
                                style={{ width: '100%' }}
                                className="input"
                                type="number"
                                title={translate('Spare beds')}
                                placeholder={translate('Spare beds')}
                                value={props.numberOfSpareBeds}
                                min={0}
                            />

                            <div
                                onClick={() => {
                                    clearHandler();
                                }}
                                className="button-primary availibility-filter-button"
                            >
                                {translate('Clear')}
                            </div>
                            {availableTo ? (
                                <div className="">
                                    <div className="checkbox-with-label-wrapper">
                                        <input
                                            onChange={(e) => props.toogleOccupiedRooms(e)}
                                            checked={props.showOccupiedRooms}
                                            id="occ-rooms-check"
                                            type="checkbox"
                                            className="checkbox-primary"
                                        ></input>
                                        <label htmlFor="occ-rooms-check">{translate('Include occupied rooms')}</label>
                                    </div>
                                </div>
                            ) : null}
                            <div
                                onClick={() => props.setShowReservationsList(!props.showReservationsList)}
                                className="button-white-w-icon occupancy-icon"
                            >
                                {/* {this.props.showReservationsList ? <ResList />  :  <Calendar /> } */}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    return (
        <div className="reservation-filters-wrapper">
            <span>{translate('Availability')}:&ensp;</span>
            <CustomDayPicker
                value={availableFrom ? new Date(availableFrom)?.getTime() : new Date().getTime()}
                onSelect={async (e: any) => {
                    handleAvailableFromDatePick(e);
                }}
                disabledBefore={new Date()}
            />

            <small>&ensp;{translate('-')}&ensp;</small>
            
            <CustomDayPicker
                value={
                    availableTo
                        ? new Date(availableTo)?.getTime()
                        : availableFrom2 === ''
                        ? new Date().getTime()
                        : new Date(availableFrom2)?.getTime()
                }
                onSelect={async (e: any) => {
                    handleAvailableToDatePick(e);
                }}
                disabledBefore={availableFrom ? new Date(availableFrom) : new Date()}
                disabledAfter={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
            />

            {/* <small>&ensp;{translate('')}&ensp;</small> */}
            <input
                style={{ width: '82px' }}
                min={0}
                value={stayInDays === null ? '' : stayInDays}
                onChange={(e: any) => changeStayInDays(e)}
                className="input"
                type="number"
                placeholder={translate('Days')}
                title={translate('Days')}
            />
            <input
                onChange={(e: any) => props.setNumberOfBeds(e)}
                style={{ width: '82px' }}
                className="input"
                type="number"
                title={translate('Beds')}
                placeholder={translate('Beds')}
                value={props.numberOfBeds}
                min={0}
            />

            <input
                onChange={(e: any) => props.setNumberOfSpareBeds(e)}
                style={{ width: '82px' }}
                className="input"
                type="number"
                title={translate('Spare beds')}
                placeholder={translate('Spare beds')}
                value={props.numberOfSpareBeds}
                min={0}
            />

            <FeatureFlag flag="groupInfos">
                <div onClick={clearHandler} className="button-primary">
                    {translate('Clear')}
                </div>
            </FeatureFlag>
            {availableTo ? (
                <div className="">
                    <div className="checkbox-with-label-wrapper">
                        <input
                            onChange={(e) => props.toogleOccupiedRooms(e)}
                            checked={props.showOccupiedRooms}
                            id="occ-rooms-check"
                            type="checkbox"
                            className="checkbox-primary"
                        ></input>
                        <label htmlFor="occ-rooms-check">{translate('Include occupied rooms')}</label>
                    </div>
                </div>
            ) : null}

            <div className="ml-auto flex-center-center">
                <div
                    className="custom-control custom-switch"
                    onClick={async () => {
                        props.setShowReservationsList(!props.showReservationsList);
                    }}
                >
                    <input
                        type="checkbox"
                        className="custom-control-input"
                        // id="customSwitch1"
                        checked={!props.showReservationsList ? true : false}
                        onChange={() => {
                            return null;
                        }}
                    />
                    <label
                        className="custom-control-label pointer"
                        style={{ backgroundColor: '#1068ec' }}
                        htmlFor=""
                    ></label>
                </div>
                <div className="font-12p mr-4p">{translate('Show summary')}</div>
            </div>
        </div>
    );
}

export default FromToFilter;
